import { useBulle } from '@/lib/use-bulle';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface MarqueBulleProps {
  /** The accessible name of the passage ("Ajout : cause inventee"). */
  nom: string;
  /** The look of the passage: a background alone (see `marque-ajout.tsx`, `marque-changement.tsx`). */
  className: string;
  /** Data attribute that tells what the passage is (`data-ajout`, `data-changement`). */
  attribut: 'data-ajout' | 'data-changement';
  /** What the bubble says. */
  bulle: ReactNode;
  children: ReactNode;
}

/**
 * A passage of the translated text of "Avant / après" explained by a bubble with what Tahiti
 * says about it (the additions of the control, #23, and the changes the translation declares,
 * #28): the same component for both, only the look, the name and the content change.
 *
 * The bubble is the one mechanism of the app (`useBulle`, WCAG 1.4.13: hover and focus, Escape,
 * stays while the pointer is on it, kept in the window), shared with the icon buttons of the
 * result card (`Infobulle`, #37). It is `fixed`, in a portal, so the edge of the text frame never
 * cuts it, and always in the page (hidden when closed) so `aria-describedby` always has its target.
 */
export function MarqueBulle({
  nom,
  className,
  attribut,
  bulle: contenu,
  children,
}: MarqueBulleProps) {
  const { cible, id, propsCible, propsBulle } = useBulle<HTMLElement>();

  return (
    <>
      <mark
        ref={cible}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: the passage takes the focus so the keyboard reaches its bubble (WCAG 1.4.13).
        tabIndex={0}
        // biome-ignore lint/a11y/useSemanticElements: a `fieldset` is a form group; this is a named passage of text.
        role="group"
        aria-label={nom}
        aria-describedby={id}
        {...{ [attribut]: '' }}
        {...propsCible}
        className={className}
      >
        {children}
      </mark>
      {createPortal(
        <div
          {...propsBulle}
          role="tooltip"
          className="fixed z-50 w-[360px] max-w-[calc(100vw-16px)] rounded-md border border-gray-300 bg-white px-4 py-3 text-left font-normal text-black text-small leading-normal shadow-md"
        >
          {contenu}
        </div>,
        document.body,
      )}
    </>
  );
}
